/**
 * Qué le falta a este vehículo — el panel que salta cuando el auto está en el
 * taller, que es cuando se vende.
 *
 * Muestra lo que reclama atención (vencido / pendiente / por vencer) y deja
 * accionarlo sin salir de la pantalla: posponer, no insistir más, o marcar que
 * el cliente lo hizo en otro lado (que arranca el reloj igual).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ExclamationTriangleIcon, ClockIcon, CheckCircleIcon, EllipsisHorizontalIcon
} from '@heroicons/react/24/outline';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Tip } from './ui/tip';

export interface PuntoServicio {
  pointKey: string;
  label: string;
  sectionLabel?: string;
  situacion: 'vencido' | 'pendiente' | 'por_vencer' | 'ok';
  motivo?: string | null;
  note?: string | null;
  state?: string | null;
  checkedAt?: string | null;
  checkedKm?: number | null;
  doneAt?: string | null;
  doneKm?: number | null;
  dueAt?: string | null;
  dueKm?: number | null;
  silenciado?: 'pospuesto' | 'no_insistir' | null;
  snoozedUntil?: string | null;
  /** Qué diagnóstico dejó este estado */
  diagnosisId?: string | null;
}

const ESTILOS: Record<string, { chip: string; icono: React.ReactNode; texto: string }> = {
  vencido: {
    chip: 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400',
    icono: <ExclamationTriangleIcon className="h-4 w-4" />,
    texto: 'Vencido'
  },
  pendiente: {
    chip: 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    icono: <ExclamationTriangleIcon className="h-4 w-4" />,
    texto: 'Pendiente'
  },
  por_vencer: {
    chip: 'border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-400',
    icono: <ClockIcon className="h-4 w-4" />,
    texto: 'Por vencer'
  }
};

const fechaCorta = (ymd?: string | null) => {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y.slice(2)}` : null;
};

export function VehicleServicePanel({
  vehicleId,
  compacto = false,
  titulo = 'Pendiente en este vehículo',
  excluirDiagnosisId,
  onCambio
}: {
  vehicleId?: string | null;
  compacto?: boolean;
  titulo?: string;
  /** Dentro de un checklist: lo que marcó ESE diagnóstico no "venía pendiente". */
  excluirDiagnosisId?: string | null;
  onCambio?: (reclaman: number) => void;
}) {
  const [reclaman, setReclaman] = useState<PuntoServicio[]>([]);
  const [kmActual, setKmActual] = useState<number | null>(null);
  const [cargando, setCargando] = useState(false);
  const [verTodo, setVerTodo] = useState(false);

  const cargar = useCallback(async () => {
    if (!vehicleId) { setReclaman([]); return; }
    setCargando(true);
    try {
      const res = await axios.get(`/api/service-status/vehicle/${vehicleId}`);
      const lista = (res.data.reclaman || []).filter(
        (p: any) => !excluirDiagnosisId || p.diagnosisId !== excluirDiagnosisId
      );
      setReclaman(lista);
      setKmActual(res.data.kmActual ?? null);
      onCambio?.(lista.length);
    } catch {
      setReclaman([]);
    } finally {
      setCargando(false);
    }
    // onCambio se deja afuera a propósito: si el padre lo redefine en cada
    // render, incluirlo dispara un bucle de recargas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, excluirDiagnosisId]);

  useEffect(() => { cargar(); }, [cargar]);

  const accion = async (p: PuntoServicio, modo: string, meses?: number) => {
    try {
      const url = modo === 'hecho-afuera'
        ? `/api/service-status/vehicle/${vehicleId}/${p.pointKey}/hecho-afuera`
        : `/api/service-status/vehicle/${vehicleId}/${p.pointKey}/snooze`;
      const body = modo === 'hecho-afuera' ? { km: kmActual } : { modo, meses };
      const res = await axios.post(url, body);
      setReclaman(res.data.reclaman || []);
      onCambio?.((res.data.reclaman || []).length);
      toast.success(
        modo === 'hecho-afuera' ? `${p.label}: registrado como hecho afuera`
          : modo === 'dismiss' ? `${p.label}: no se avisa más`
          : `${p.label}: pospuesto ${meses} meses`
      );
    } catch {
      toast.error('No se pudo actualizar');
    }
  };

  if (!vehicleId) return null;
  if (!cargando && reclaman.length === 0) {
    if (compacto) return null;
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-muted-foreground">
          <CheckCircleIcon className="h-4 w-4 text-emerald-500 shrink-0" />
          <span>Sin pendientes registrados en este vehículo</span>
        </div>
      </div>
    );
  }

  const visibles = verTodo ? reclaman : reclaman.slice(0, compacto ? 3 : 6);

  return (
    <div className="space-y-2">
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.04] overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-amber-500/20 px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 shrink-0" />
          <span className="text-sm font-semibold text-foreground truncate">{titulo}</span>
          <span className="shrink-0 rounded-full bg-amber-500/15 px-2 text-xs font-semibold text-amber-600 dark:text-amber-400">
            {reclaman.length}
          </span>
        </div>
        {kmActual ? (
          <span className="shrink-0 text-xs text-muted-foreground">{kmActual.toLocaleString('es-AR')} km</span>
        ) : null}
      </div>

      <ul className="divide-y divide-border/60">
        {visibles.map((p) => {
          const est = ESTILOS[p.situacion] || ESTILOS.pendiente;
          return (
            <li key={p.pointKey} className="flex items-start gap-2 px-3 py-2">
              <span className={`mt-0.5 shrink-0 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${est.chip}`}>
                {est.icono}
                <span className="cq-hide-narrow">{est.texto}</span>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">{p.label}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {p.motivo}
                  {p.checkedAt ? ` · visto ${fechaCorta(p.checkedAt)}` : ''}
                  {p.doneAt ? ` · hecho ${fechaCorta(p.doneAt)}` : ''}
                </p>
                {p.note && p.note !== p.label && (
                  <p className="text-xs text-muted-foreground/80 truncate">{p.note}</p>
                )}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
                    <EllipsisHorizontalIcon className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {/* Solo para trabajo hecho AFUERA: lo que se hace acá lo marca
                      el remito al emitirse, sin que nadie toque nada. */}
                  <DropdownMenuItem onClick={() => accion(p, 'hecho-afuera')}>
                    Lo hizo en otro taller
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => accion(p, 'posponer', 3)}>Posponer 3 meses</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => accion(p, 'posponer', 6)}>Posponer 6 meses</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => accion(p, 'dismiss')} className="text-destructive">
                    No insistir más
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          );
        })}
      </ul>

      {reclaman.length > visibles.length && (
        <button
          onClick={() => setVerTodo(true)}
          className="w-full border-t border-border/60 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          Ver los {reclaman.length - visibles.length} restantes
        </button>
      )}
    </div>
    </div>
  );
}

/** Chip chico para listas: solo el número, sin ocupar lugar. */
export function VehicleServiceBadge({ cantidad }: { cantidad: number }) {
  if (!cantidad) return null;
  return (
    <Tip label={`${cantidad} punto(s) pendientes o vencidos`}>
      <span className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
        <ExclamationTriangleIcon className="h-3 w-3" />
        {cantidad}
      </span>
    </Tip>
  );
}

export default VehicleServicePanel;
